import { computed, getCurrentScope, onBeforeUnmount, onScopeDispose, ref, shallowRef, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { isAxiosError } from 'axios';
import { compileMatrix, sumChanges, type CompiledForm } from '@record-fill';
import {
  GENERATED_RECORD_LOCK_CHANGED_EVENT,
  GENERATED_RECORD_NOT_FOUND_MESSAGE,
  getGeneratedRecordHistory,
  isGeneratedRecordLockEvent,
  recordTitleOf,
  type GeneratedRecordHistory,
} from '@/entities/GeneratedRecord';
import { useAuthStore } from '@/entities/Session';
import { onRealtimeEvent } from '@/shared/api';
import {
  clearBreadcrumbTrail,
  isNotFoundError,
  MATRIX_RETURN_FOLDER_QUERY,
  setBreadcrumbTrail,
  useCustomToast,
} from '@/shared/lib';
import { buildErpEntry, ERP_ENTRY_NUMBER, erpFieldIds } from './fill-logs-erp';
import type { CorrectedWord } from './fill-logs-text';
import { buildResponseViews, correctedWordOf, type FillLogsResponseView } from './fill-logs-timeline';
import { buildMatrixTitle } from './matrix-title';
import { titlesAtResponse } from './record-indexing';
import {
  buildGenerateRecordTrail,
  FILL_LOGS_BREADCRUMB,
  QUALITY_RECORDS_BREADCRUMB,
} from './record-fill.breadcrumb';
import { matrixPageLocation } from './record-fill.return-target';

export const FILL_LOGS_LOAD_ERROR = 'Erro ao carregar logs de preenchimento. Tente novamente.';
export const CORRECTED_LEGEND = 'Corrigido com Ctrl+F8';
export const CHANGED_LEGEND = 'Alterado nesta resposta';
const FILL_LOGS_ROUTE = 'LogsDePreenchimento';

const REFUSAL_STATUSES = new Set([400, 403, 404]);

// Read only hides every issue, so no pattern needs testing: a hostile pattern would only block the
// main thread for a verdict nobody sees.
const skipPatternTest = () => true;

export function useFillLogsPage(
  prefix: Ref<string>,
  recordId: Ref<string>,
  returnFolderCode: string | null = null
) {
  const router = useRouter();
  const authStore = useAuthStore();
  const { showErrorToast } = useCustomToast();

  const history = shallowRef<GeneratedRecordHistory | null>(null);
  const loading = ref(true);
  const error = ref(false);
  const selectedNumber = ref(0);
  const showHighlights = ref(true);
  let latestRequest = 0;
  let latestRefresh = 0;
  let refreshOwed = false;

  const title = computed(() => (history.value ? buildMatrixTitle(history.value.matrix) : ''));
  const recordLabel = computed(() => (history.value ? recordTitleOf(history.value.record) : ''));
  const erp = computed(() => (history.value ? buildErpEntry(history.value, { patternTest: skipPatternTest }) : null));
  const responses = computed<FillLogsResponseView[]>(() => {
    const loaded = history.value;
    if (!loaded) return [];
    return buildResponseViews(loaded, erp.value ? erpFieldIds(loaded) : []);
  });
  const total = computed(() => responses.value.length);
  const hasErpEntry = computed(() => erp.value !== null);
  const erpCompiled = computed(() => erp.value?.compiled ?? null);
  const erpView = computed(() => erp.value?.view ?? null);
  const firstNumber = computed(() => (erpView.value ? ERP_ENTRY_NUMBER : 1));
  const current = computed(() =>
    selectedNumber.value === ERP_ENTRY_NUMBER ? erpView.value : (responses.value[selectedNumber.value - 1] ?? null),
  );
  const isLatest = computed(() => selectedNumber.value === total.value);
  const timeline = computed(() => {
    const newestFirst = [...responses.value].reverse();
    return erpView.value ? [...newestFirst, erpView.value] : newestFirst;
  });

  // The photo and the rules both belong to response N: a rule that hides a field today may have
  // shown it then, so neither the latest answers nor the latest protected rules may be used. Only
  // the chosen response is compiled; a compiled photo per response grew with responses times rows.
  const compiled = computed<CompiledForm | null>(() => {
    const loaded = history.value;
    const count = selectedNumber.value;
    if (count === ERP_ENTRY_NUMBER) return erpCompiled.value;
    const chosen = loaded?.responses[count - 1];
    if (!loaded || !chosen) return null;
    const changes = loaded.responses.slice(0, count).map((response) => response.changes);
    const answers = sumChanges(loaded.matrix.id, changes);
    return compileMatrix({
      structure: loaded.structure,
      rules: loaded.rules,
      permissions: loaded.permissions,
      subject: loaded.subject,
      answers,
      options: { patternTest: skipPatternTest },
      titles: titlesAtResponse(loaded, answers.values),
    });
  });

  const changeMarks = computed(() => current.value?.marks ?? null);
  const correctedLegend = computed(() => (current.value?.correction ? CORRECTED_LEGEND : CHANGED_LEGEND));
  const correctedWord = computed<CorrectedWord>(() => correctedWordOf(current.value));

  function select(number: number): void {
    if (timeline.value.length === 0) return;
    selectedNumber.value = Math.min(Math.max(number, firstNumber.value), total.value);
  }

  function selectLatest(): void {
    select(total.value);
  }

  function selectNewer(): void {
    select(selectedNumber.value + 1);
  }

  function selectOlder(): void {
    select(selectedNumber.value - 1);
  }

  async function reportLoadFailure(failure: unknown): Promise<void> {
    if (isAxiosError(failure) && REFUSAL_STATUSES.has(failure.response?.status ?? 0)) {
      showErrorToast({
        title: FILL_LOGS_LOAD_ERROR,
        type: 'error',
        ...(isNotFoundError(failure) ? { message: GENERATED_RECORD_NOT_FOUND_MESSAGE } : {}),
      });
      await router.replace(
        matrixPageLocation(prefix.value, returnFolderCode, { generatedRecords: true })
      );
      return;
    }
    error.value = true;
  }

  async function load(): Promise<void> {
    // Only the newest request may publish: a late answer for the previous id would paint another
    // record's history.
    const request = ++latestRequest;

    loading.value = true;
    error.value = false;
    refreshOwed = false;
    history.value = null;
    selectedNumber.value = 0;
    setBreadcrumbTrail([QUALITY_RECORDS_BREADCRUMB]);

    try {
      const loaded = await getGeneratedRecordHistory(Number(recordId.value));
      if (request !== latestRequest) return;
      history.value = loaded;
      selectedNumber.value = loaded.responses.length;
      if (refreshOwed) {
        refreshOwed = false;
        void refresh();
      }
      setBreadcrumbTrail(
        buildGenerateRecordTrail(loaded.matrix.prefix, title.value, FILL_LOGS_BREADCRUMB, returnFolderCode)
      );
      if (loaded.matrix.prefix !== prefix.value) {
        await router.replace({
          name: FILL_LOGS_ROUTE,
          params: { prefix: loaded.matrix.prefix, recordId: recordId.value },
          ...(returnFolderCode ? { query: { [MATRIX_RETURN_FOLDER_QUERY]: returnFolderCode } } : {}),
        });
      }
    } catch (failure) {
      if (request !== latestRequest) return;
      await reportLoadFailure(failure);
    } finally {
      if (request === latestRequest) loading.value = false;
    }
  }

  // A new response arrives while the page is open: the history reloads in place. Whoever is on the
  // latest response when the reload lands follows the new latest, even if they went there while it
  // was in flight; an older response stays the one on screen.
  async function refresh(): Promise<void> {
    if (!history.value) {
      // The first read may have left the server before the response this event announces.
      if (loading.value) refreshOwed = true;
      return;
    }
    const loadRequest = latestRequest;
    const request = ++latestRefresh;

    try {
      const loaded = await getGeneratedRecordHistory(Number(recordId.value));
      if (loadRequest !== latestRequest || request !== latestRefresh) return;
      // Read before the swap: once the new history is in, the old latest is no longer the latest.
      const followLatest = isLatest.value;
      history.value = loaded;
      const count = loaded.responses.length;
      selectedNumber.value = followLatest ? count : Math.min(selectedNumber.value, count);
    } catch {
      // The screen keeps the history it already shows; the next event or a reload tries again.
    }
  }

  // The event carries only the id; the history comes back through the route, which checks access.
  const stopLockEvents = onRealtimeEvent(GENERATED_RECORD_LOCK_CHANGED_EVENT, (event: unknown) => {
    if (isGeneratedRecordLockEvent(event) && event.generatedRecordId === Number(recordId.value)) {
      void refresh();
    }
  });
  if (getCurrentScope()) onScopeDispose(stopLockEvents);

  watch(recordId, load, { immediate: true });

  watch(
    () => authStore.selectedBranchId,
    (next, previous) => {
      if (next !== previous) {
        latestRequest += 1;
        router.replace({ name: 'Home' });
      }
    }
  );

  onBeforeUnmount(() => {
    // The trail override is a module global and the request outlives the component.
    latestRequest += 1;
    clearBreadcrumbTrail();
  });

  return {
    loading,
    error,
    title,
    recordLabel,
    timeline,
    total,
    hasErpEntry,
    current,
    isLatest,
    selectedNumber,
    showHighlights,
    compiled,
    changeMarks,
    correctedLegend,
    correctedWord,
    load,
    select,
    selectLatest,
    selectNewer,
    selectOlder,
  };
}
